import { Link } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CalendarRange,
  Download,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { BillingPeriod } from "@/hooks/use-pay-applications";
import {
  useBillingPeriods,
  useCreateBillingPeriod,
  useDeleteBillingPeriod,
  useUpdateBillingPeriod,
} from "@/hooks/use-pay-applications";

type SortKey = "periodStart" | "periodEnd" | "status" | "createdAt";

function formatDate(value: string | null | undefined): string {
  if (!value) return "--";
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.exec(value);
  const d = dateOnly ? new Date(`${value}T00:00:00`) : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function PeriodDialog({
  period,
  open,
  onOpenChange,
}: {
  period: BillingPeriod | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const isEdit = period !== null;
  const create = useCreateBillingPeriod();
  const update = useUpdateBillingPeriod();

  const [periodStart, setPeriodStart] = useState(period?.periodStart ?? "");
  const [periodEnd, setPeriodEnd] = useState(period?.periodEnd ?? "");
  const [status, setStatus] = useState(period?.status ?? "OPEN");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!periodStart || !periodEnd) return;
    if (periodEnd < periodStart) return;
    if (isEdit && period) {
      update.mutate(
        { id: period.id, periodStart, periodEnd, status },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      create.mutate(
        { periodStart, periodEnd, status },
        {
          onSuccess: () => {
            onOpenChange(false);
            setPeriodStart("");
            setPeriodEnd("");
            setStatus("OPEN");
          },
        },
      );
    }
  }

  const isPending = isEdit ? update.isPending : create.isPending;
  const invalidRange = !!periodStart && !!periodEnd && periodEnd < periodStart;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[50vw]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Billing Period" : "New Billing Period"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="bp-start">Period Start</Label>
              <Input
                id="bp-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bp-end">Period End</Label>
              <Input
                id="bp-end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                required
              />
            </div>
          </div>
          {invalidRange ? (
            <p className="text-xs text-destructive">Period end must be on or after period start.</p>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="bp-status">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger id="bp-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OPEN">Open</SelectItem>
                <SelectItem value="CLOSED">Closed</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Only OPEN periods are selectable when creating a pay application.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending || !periodStart || !periodEnd || invalidRange}
            >
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Create Period"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BillingPeriodsPage() {
  const { data, isLoading, isError, error, refetch } = useBillingPeriods();
  const deleteMutation = useDeleteBillingPeriod();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<BillingPeriod | null>(null);
  const [periodToDelete, setPeriodToDelete] = useState<BillingPeriod | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("periodStart");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [sortedColumn, setSortedColumn] = useState<SortKey | null>(null);

  const items = data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      [p.periodStart, p.periodEnd, p.status].join(" ").toLowerCase().includes(q),
    );
  }, [items, search]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (sortedColumn === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortedColumn(key);
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortedColumn !== col) return null;
    return sortDir === "asc" ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    );
  }

  function downloadCSV() {
    const headers = ["Period Start", "Period End", "Status", "Created"];
    const rows = sorted.map((p) => [p.periodStart, p.periodEnd, p.status, p.createdAt]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "billing-periods.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={error?.message} onRetry={() => refetch()} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Billing Periods"
        description="Manage AIA billing periods used by pay applications"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/admin/config">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Configuration
              </Link>
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Period
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by date or status..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
        <Button variant="outline" onClick={downloadCSV} disabled={sorted.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {sorted.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <CalendarRange className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold">No billing periods yet</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Create a billing period so finance can roll the calendar forward and pay applications
              can be assigned to it.
            </p>
            <Button className="mt-4" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Period
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => handleSort("periodStart")}>
                Period Start
                <SortIcon col="periodStart" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("periodEnd")}>
                Period End
                <SortIcon col="periodEnd" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("status")}>
                Status
                <SortIcon col="status" />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort("createdAt")}>
                Created
                <SortIcon col="createdAt" />
              </TableHead>
              <TableHead className="w-32 text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{formatDate(p.periodStart)}</TableCell>
                <TableCell>{formatDate(p.periodEnd)}</TableCell>
                <TableCell>
                  <Badge variant={p.status === "OPEN" ? "default" : "secondary"}>{p.status}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatDate(p.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => setEditingPeriod(p)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => setPeriodToDelete(p)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PeriodDialog period={null} open={createOpen} onOpenChange={setCreateOpen} />
      {editingPeriod ? (
        <PeriodDialog
          period={editingPeriod}
          open={true}
          onOpenChange={(o) => {
            if (!o) setEditingPeriod(null);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={periodToDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPeriodToDelete(null);
        }}
        title="Delete billing period?"
        description={
          periodToDelete
            ? `${formatDate(periodToDelete.periodStart)} – ${formatDate(periodToDelete.periodEnd)} will be removed. Periods referenced by pay applications cannot be deleted.`
            : ""
        }
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (periodToDelete) {
            deleteMutation.mutate(periodToDelete.id, {
              onSuccess: () => setPeriodToDelete(null),
              onError: () => setPeriodToDelete(null),
            });
          }
        }}
      />
    </div>
  );
}
